import React, { useState } from "react";
import { CheckCircle2, ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import { Switch } from "../../ui/switch";
import { mk } from "../../../lib/marketApi";
import { Card, inputCls, Labeled } from "./ui";

const Toggle = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3.5 py-2.5">
    <span className="text-sm text-slate-700">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export const DeliveryTab = ({ draft, setDraft }) => {
  const sh = draft.shipping || {};
  const c = draft.contact || {};
  const setSh = (k, v) => setDraft((d) => ({ ...d, shipping: { ...d.shipping, [k]: v } }));
  const setC = (k, v) => setDraft((d) => ({ ...d, contact: { ...d.contact, [k]: v } }));
  return (
    <div className="space-y-4">
      <Card title="Formas de entrega">
        <Toggle label="Envío a domicilio" checked={sh.delivery} onChange={(v) => setSh("delivery", v)} testid="ship-delivery-switch" />
        {sh.delivery && (
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Costo de envío"><input type="number" min="0" className={inputCls} value={sh.delivery_fee ?? 0} onChange={(e) => setSh("delivery_fee", e.target.value)} data-testid="ship-fee-input" /></Labeled>
            <Labeled label="Gratis desde" hint="0 = nunca gratis"><input type="number" min="0" className={inputCls} value={sh.free_over ?? 0} onChange={(e) => setSh("free_over", e.target.value)} data-testid="ship-free-input" /></Labeled>
          </div>
        )}
        <Toggle label="Recoger en tienda" checked={sh.pickup} onChange={(v) => setSh("pickup", v)} testid="ship-pickup-switch" />
        {sh.pickup && <Labeled label="Dirección para recoger"><input className={inputCls} value={sh.pickup_address || ""} onChange={(e) => setSh("pickup_address", e.target.value)} data-testid="ship-pickup-address" /></Labeled>}
      </Card>
      <Card title="Contacto">
        <Labeled label="WhatsApp (con lada)"><input className={inputCls} value={c.whatsapp || ""} onChange={(e) => setC("whatsapp", e.target.value)} data-testid="contact-whatsapp" /></Labeled>
        <Labeled label="Correo"><input className={inputCls} value={c.email || ""} onChange={(e) => setC("email", e.target.value)} data-testid="contact-email" /></Labeled>
        <Labeled label="Dirección"><input className={inputCls} value={c.address || ""} onChange={(e) => setC("address", e.target.value)} data-testid="contact-address" /></Labeled>
      </Card>
      <Card title="Dominio y estado">
        <Labeled label="Dominio propio (opcional)" hint="Ej. mitienda.com — apunta el dominio a UAICARD igual que en los portafolios">
          <input className={inputCls} value={draft.domain || ""} onChange={(e) => setDraft((d) => ({ ...d, domain: e.target.value }))} placeholder="mitienda.com" data-testid="store-domain-input" />
        </Labeled>
        <Toggle label="Tienda activa" checked={draft.status !== "paused"} onChange={(v) => setDraft((d) => ({ ...d, status: v ? "active" : "paused" }))} testid="store-active-switch" />
      </Card>
    </div>
  );
};

export const PaymentsTab = ({ sid, token, store, onChange }) => {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await mk(`/owner/${sid}/stripe`, { method: "PUT", token, body: { secret_key: key } });
      setKey("");
      onChange();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm("¿Desconectar Stripe? La tienda dejará de cobrar en línea.")) return;
    await mk(`/owner/${sid}/stripe`, { method: "DELETE", token });
    onChange();
  };
  return (
    <div className="space-y-4">
      <Card title="Cobros con Stripe">
        <p className="text-sm text-slate-600">El dinero de cada venta llega directo a la cuenta Stripe del dueño de esta tienda.</p>
        {store.payments_ready ? (
          <div className="flex items-center justify-between gap-3 rounded-xl bg-emerald-50 p-3.5" data-testid="stripe-connected">
            <span className="flex items-center gap-2 text-sm font-medium text-emerald-800">
              <CheckCircle2 className="h-4 w-4" /> Conectado · {store.stripe_key_hint}
            </span>
            <button onClick={remove} className="text-xs text-red-600 underline" data-testid="stripe-disconnect">Desconectar</button>
          </div>
        ) : (
          <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="stripe-not-connected">Aún no está conectado: los compradores no pueden pagar.</p>
        )}
        <Labeled label={store.payments_ready ? "Cambiar clave secreta" : "Clave secreta de Stripe"} hint="La encuentras en Stripe → Desarrolladores → Claves de API (empieza con sk_live_). Se guarda cifrada y nunca se muestra completa.">
          <input type="password" autoComplete="off" className={inputCls} value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk_live_…" data-testid="stripe-key-input" />
        </Labeled>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <button disabled={!key || busy} onClick={save} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50" data-testid="stripe-save-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Guardar clave
        </button>
        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600">
          Abrir Stripe <ExternalLink className="h-3 w-3" />
        </a>
      </Card>
    </div>
  );
};
